import { useMemo, useState } from 'react';
import Card from '@mui/material/Card';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function hm(min) {
  const m = Number(min || 0);
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, '0');
  return `${String(h).padStart(2, '0')}:${mm}`;
}

function participantsLabel(participants = []) {
  if (!participants?.length) return 'Katılımcı yok';

  // "Ad Soyad, Ad Soyad" formatı
  const names = participants.map((p) => p?.fullName).filter(Boolean);

  if (!names.length) return 'Katılımcı yok';

  // çok uzarsa kısaltalım (UI taşmasın)
  const joined = names.join(', ');
  return joined.length > 48 ? `${joined.slice(0, 48)}…` : joined;
}

export default function PilatesTodaySessionsCard({
  sessions = [],
  trainers = [], // opsiyonel
}) {
  // null => tüm eğitmenler
  const [trainerId, setTrainerId] = useState(null);

  // trainers prop gelmezse session'lardan çıkar
  const trainerOptions = useMemo(() => {
    if (trainers.length) return trainers;

    const map = new Map();
    sessions.forEach((s) => {
      if (s?.trainerId && !map.has(s.trainerId)) {
        map.set(s.trainerId, {
          id: s.trainerId,
          fullName: s.trainerName || `Eğitmen #${s.trainerId}`,
        });
      }
    });
    return Array.from(map.values());
  }, [sessions, trainers]);

  const filteredSessions = useMemo(() => {
    if (!trainerId) return sessions;
    return sessions.filter((s) => s.trainerId === trainerId);
  }, [sessions, trainerId]);

  return (
    <Card sx={{ height: '100%' }}>
      {/* HEADER */}
      <MDBox
        p={3}
        pb={2}
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
      >
        <MDBox>
          <MDTypography variant="h6" fontWeight="bold">
            Bugünkü Seanslar
          </MDTypography>
          <MDTypography variant="button" color="text" sx={{ opacity: 0.75 }}>
            Yaklaşan randevular ve dersler
          </MDTypography>
        </MDBox>

        {/* SAĞ TARAF: EĞİTMEN SEÇİMİ */}
        <MDBox display="flex" gap={0.75} flexWrap="wrap" justifyContent="flex-end">
          <FilterChip active={trainerId === null} label="Tümü" onClick={() => setTrainerId(null)} />

          {trainerOptions.map((t) => (
            <FilterChip
              key={t.id}
              active={trainerId === t.id}
              label={t.fullName}
              onClick={() => setTrainerId(t.id)}
            />
          ))}
        </MDBox>
      </MDBox>

      {/* LIST */}
      <MDBox px={3} pb={3} sx={{ display: 'grid', gap: 1 }}>
        {filteredSessions.length === 0 ? (
          <MDTypography variant="button" color="text" sx={{ opacity: 0.6 }}>
            Seans bulunamadı.
          </MDTypography>
        ) : (
          filteredSessions.map((x) => {
            const names = participantsLabel(x.participants);
            const activity = x.activityName || 'Pilates';
            const trainer = x.trainerName || 'Eğitmen';

            return (
              <MDBox
                key={x.sessionId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '110px 1fr' },
                  gap: 1,
                  p: 1.25,
                  borderRadius: '14px',
                  border: '1px solid rgba(0,0,0,0.08)',
                  background: 'rgba(255,255,255,0.75)',
                }}
              >
                {/* SAAT */}
                <MDTypography variant="button" fontWeight="bold">
                  {hm(x.startMin)}
                </MDTypography>

                {/* İÇERİK */}
                <MDBox
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                  }}
                >
                  {/* ✅ KATILIMCILAR */}
                  <MDTypography variant="button" fontWeight="medium">
                    {names}
                  </MDTypography>

                  {/* ✅ "PACKAGE" yerine "Pilates" yaz */}
                  <MDTypography variant="caption" color="text" sx={{ opacity: 0.75 }}>
                    {activity} • {trainer}
                  </MDTypography>
                </MDBox>
              </MDBox>
            );
          })
        )}
      </MDBox>
    </Card>
  );
}

/* 🔹 Küçük, şık chip */
function FilterChip({ label, active, onClick }) {
  return (
    <MDBox
      onClick={onClick}
      sx={{
        px: 1.25,
        py: 0.5,
        borderRadius: '999px',
        fontSize: 12,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        border: '1px solid',
        borderColor: active ? 'info.main' : 'rgba(0,0,0,0.15)',
        color: active ? 'info.main' : 'text.secondary',
        background: active ? 'rgba(26,115,232,0.08)' : 'transparent',
        transition: 'all .2s',
        '&:hover': { background: 'rgba(26,115,232,0.12)' },
      }}
    >
      {label}
    </MDBox>
  );
}
